import { useEffect, useState, useCallback } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Users, Truck, Refrigerator, Cable, Download, Tag, Save, Share2, Copy, Eye, Pencil, X, Usb, Crosshair } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getBridgeUrl, setBridgeUrl } from "@/lib/kitchenBridge";
import { enqueuePrint } from "@/lib/printHub";
import InstallPwa from "@/components/InstallPwa";
import NotificationsCard from "@/components/NotificationsCard";

export default function Settings() {
  const { activeSite, refresh: refreshSites } = useSite();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      <PageHeader
        title="Settings"
        subtitle="Manage sites, users, suppliers and equipment. Admins only."
      />
      <div className="mb-6 grid lg:grid-cols-2 gap-6">
        <InstallPwa />
        <NotificationsCard siteId={activeSite?.id || ""} />
      </div>
      {!isAdmin ? (
        <div className="surface p-10 text-center text-charcoal-soft">
          Only admins can change settings.
        </div>
      ) : (
        <Tabs defaultValue="sites">
          <TabsList className="mb-6">
            <TabsTrigger data-testid="tab-sites" value="sites">Sites</TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users">Users</TabsTrigger>
            <TabsTrigger data-testid="tab-suppliers" value="suppliers">Suppliers</TabsTrigger>
            <TabsTrigger data-testid="tab-equipment" value="equipment">Equipment</TabsTrigger>
            <TabsTrigger data-testid="tab-bridge" value="bridge">Kitchen Bridge</TabsTrigger>
          </TabsList>
          <TabsContent value="sites"><SitesPanel onChange={refreshSites} /></TabsContent>
          <TabsContent value="users"><UsersPanel /></TabsContent>
          <TabsContent value="suppliers">
            {activeSite && <SuppliersPanel siteId={activeSite.id} />}
          </TabsContent>
          <TabsContent value="equipment">
            {activeSite && <EquipmentPanel siteId={activeSite.id} />}
          </TabsContent>
          <TabsContent value="bridge"><BridgePanel /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function SitesPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", type: "restaurant", address: "", manager: "", printer_host: "", printer_port: 9100 });
  const load = async () => {
    const { data } = await api.get("/sites");
    setItems(data);
  };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sites", { ...f, printer_port: parseInt(f.printer_port) || 9100 });
      toast.success("Site added");
      setF({ name: "", type: "restaurant", address: "", manager: "", printer_host: "", printer_port: 9100 });
      load(); onChange?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    await api.delete(`/sites/${id}`); toast.success("Deleted"); load(); onChange?.();
  };
  const updatePrinter = async (site, printer_host, printer_port, printer_dialect) => {
    try {
      await api.patch(`/sites/${site.id}`, {
        printer_host,
        printer_port: parseInt(printer_port) || 9100,
        ...(printer_dialect ? { printer_dialect } : {}),
      });
      toast.success("Printer updated");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="surface p-6 grid md:grid-cols-6 gap-3">
        <div className="md:col-span-6 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-eucalyptus-700" />
          <h3 className="font-display font-bold">Add site</h3>
        </div>
        <Input data-testid="s-name" placeholder="Name" value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12" required />
        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
          <SelectTrigger data-testid="s-type" className="h-12"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="restaurant">Restaurant</SelectItem>
            <SelectItem value="cafe">Café</SelectItem>
            <SelectItem value="catering">Catering</SelectItem>
            <SelectItem value="mining_camp">Mining camp</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="s-addr" placeholder="Address" value={f.address}
          onChange={(e) => setF({ ...f, address: e.target.value })} className="h-12" />
        <Input data-testid="s-mgr" placeholder="Manager" value={f.manager}
          onChange={(e) => setF({ ...f, manager: e.target.value })} className="h-12" />
        <Input data-testid="s-printer" placeholder="Printer IP (optional)" value={f.printer_host}
          onChange={(e) => setF({ ...f, printer_host: e.target.value })} className="h-12 font-mono" />
        <Button type="submit" data-testid="s-submit"
          className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </form>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((s) => (
          <SiteCard key={s.id} site={s} onDelete={del} onUpdatePrinter={updatePrinter} />
        ))}
      </div>
    </div>
  );
}

function SiteCard({ site, onDelete, onUpdatePrinter }) {
  const [host, setHost] = useState(site.printer_host || "");
  const [port, setPort] = useState(site.printer_port || 9100);
  const [dialect, setDialect] = useState(site.printer_dialect || "escpos");
  const [width, setWidth] = useState(site.label_width_mm ?? 80);
  const [height, setHeight] = useState(site.label_height_mm ?? 0);
  const [orientation, setOrientation] = useState(site.label_orientation || "portrait");
  const [offsetX, setOffsetX] = useState(site.label_offset_x_mm ?? 0);
  const [offsetY, setOffsetY] = useState(site.label_offset_y_mm ?? 0);
  const [contStyle, setContStyle] = useState(site.continuation_style || "frac");
  const [trustOn, setTrustOn] = useState(!!site.public_trust_enabled);
  const [busy, setBusy] = useState(false);
  const trustUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/trust/${site.id}`;

  const PRESETS = [
    { label: "80×0 docket roll", w: 80, h: 0 },
    { label: "60×40 freezer",    w: 60, h: 40 },
    { label: "40×30 shelf tag",  w: 40, h: 30 },
    { label: "102×152 shipping", w: 102, h: 152 },
  ];

  const toggleTrust = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/sites/${site.id}`, { public_trust_enabled: next });
      setTrustOn(next);
      toast.success(next ? "Public trust page enabled" : "Public trust page disabled");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveLabelSize = async () => {
    const w = parseInt(width, 10);
    const h = parseInt(height, 10);
    if (Number.isNaN(w) || w < 20 || w > 210) {
      toast.error("Label width must be between 20 and 210 mm");
      return;
    }
    if (Number.isNaN(h) || h < 0 || h > 297) {
      toast.error("Label height must be between 0 (auto) and 297 mm");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/sites/${site.id}`, {
        label_width_mm: w, label_height_mm: h,
        label_orientation: orientation,
        continuation_style: contStyle,
        label_offset_x_mm: parseFloat(offsetX) || 0,
        label_offset_y_mm: parseFloat(offsetY) || 0,
      });
      toast.success(`Label size saved (${w}mm × ${h ? h + "mm" : "auto"}, ${orientation})`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Persist just the calibration offset (used by the +/- nudge buttons).
  const saveCalibration = async (nx, ny) => {
    const x = Math.max(-20, Math.min(20, parseFloat(nx) || 0));
    const y = Math.max(-20, Math.min(20, parseFloat(ny) || 0));
    setOffsetX(x);
    setOffsetY(y);
    setBusy(true);
    try {
      await api.patch(`/sites/${site.id}`, { label_offset_x_mm: x, label_offset_y_mm: y });
      toast.success(`Calibration saved (X ${x >= 0 ? "+" : ""}${x}mm, Y ${y >= 0 ? "+" : ""}${y}mm)`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Fire off a single dummy sticker so managers can dial in the size
  // without wasting a batch of real product on mis-scaled labels.
  // "Batch print test" — enqueue the 60×50 calibration label to the Print
  // Hub so managers can dial in roll alignment from any device.
  const testPrintHub = async () => {
    try {
      await enqueuePrint(site.id, {
        label_type: "test",
        product_name: "Calibration test",
        copies: 1,
        payload: { label_type: "test", copies: 1 },
      });
      toast.success("Calibration label sent to Print Hub");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message || "Couldn't queue test label");
    }
  };

  const copyTrustUrl = async () => {
    try {
      await navigator.clipboard.writeText(trustUrl);
      toast.success("Trust URL copied to clipboard");
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  return (
    <div className="surface p-4 flex items-start justify-between gap-3" data-testid={`site-card-${site.id}`}>
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow">{site.type.replace("_", " ")}</div>
        <div className="font-display font-bold mt-1">{site.name}</div>
        <div className="text-xs text-charcoal-soft mt-1">{site.address}</div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Input
            data-testid={`s-printer-host-${site.id}`}
            placeholder="Printer IP (e.g. 192.168.1.50)"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            className="h-10 font-mono col-span-2"
          />
          <Input
            data-testid={`s-printer-port-${site.id}`}
            placeholder="9100"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            className="h-10 font-mono"
          />
          <Button
            data-testid={`s-printer-save-${site.id}`}
            size="sm"
            onClick={() => onUpdatePrinter(site, host, port, dialect)}
            className="col-span-3 h-9 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
          >
            Save printer config
          </Button>
        </div>

        {/* Printer dialect — ESC/POS = receipt printers (Epson TM,
            Star TSP). TSPL = shipping-label printers (TSC, Gprinter,
            Xprinter, Godex). Send the wrong dialect and the unit
            spits out a page of garbled characters, so we surface it
            prominently. */}
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`s-printer-dialect-${site.id}`}>
          <span className="label-eyebrow">Printer language</span>
          {[
            { v: "escpos", label: "ESC/POS (docket)" },
            { v: "tspl",   label: "TSPL (label)" },
          ].map((d) => (
            <button
              key={d.v}
              type="button"
              data-testid={`s-printer-dialect-${site.id}-${d.v}`}
              onClick={() => setDialect(d.v)}
              className={`text-[11px] font-mono px-3 py-1 rounded border ${
                dialect === d.v
                  ? "bg-eucalyptus-900 text-white border-eucalyptus-900"
                  : "border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100"
              }`}
            >
              {d.label}
            </button>
          ))}
          <span className="text-[11px] text-charcoal-soft">
            ESC/POS = 80mm receipt rolls. TSPL = die-cut freezer / shipping stickers.
          </span>
        </div>

        {/* Label size — applied to the browser-print popups so freezer
            40×30 stickers don't get scaled up to the docket-tape 80mm
            default. Height=0 means auto (works on continuous rolls). */}
        <div className="mt-4 pt-3 border-t border-[color:var(--border)]">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-4 h-4 text-eucalyptus-700" />
            <span className="label-eyebrow">Label size (mm)</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              data-testid={`s-label-width-${site.id}`}
              type="number" inputMode="numeric" min="20" max="210" step="1"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              className="h-10 font-mono flex-1 min-w-0 no-spin text-center text-base"
              title="Label width in millimetres (20–210)"
              aria-label="Label width (mm)"
            />
            <span className="text-charcoal-soft font-mono shrink-0 text-xs">mm ×</span>
            <Input
              data-testid={`s-label-height-${site.id}`}
              type="number" inputMode="numeric" min="0" max="297" step="1"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              className="h-10 font-mono flex-1 min-w-0 no-spin text-center text-base"
              title="Label height in millimetres. 0 = auto (continuous rolls)"
              aria-label="Label height (mm, 0 = auto)"
            />
            <span className="text-charcoal-soft font-mono shrink-0 text-xs">mm</span>
          </div>

          {/* Presets — one tap sets both dimensions to a common label size */}
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`s-label-presets-${site.id}`}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                data-testid={`s-label-preset-${site.id}-${p.w}x${p.h}`}
                onClick={() => { setWidth(p.w); setHeight(p.h); }}
                className="text-[11px] font-mono px-2 py-1 rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Orientation — landscape swaps w/h at print time for
              long-edge-feed rolls (typical of 4×6" shipping labels). */}
          <div className="flex items-center gap-3 mt-3">
            <span className="label-eyebrow">Orientation</span>
            <div className="flex gap-1" data-testid={`s-label-orient-${site.id}`}>
              {["portrait", "landscape"].map((o) => (
                <button
                  key={o}
                  type="button"
                  data-testid={`s-label-orient-${site.id}-${o}`}
                  onClick={() => setOrientation(o)}
                  className={`text-[11px] font-mono px-3 py-1 rounded border ${
                    orientation === o
                      ? "bg-eucalyptus-900 text-white border-eucalyptus-900"
                      : "border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          {/* Continuation marker style for labels that spill onto a 2nd label */}
          <div className="flex items-center gap-3 mt-3">
            <span className="label-eyebrow">Continuation</span>
            <div className="flex gap-1" data-testid={`s-cont-style-${site.id}`}>
              {[["frac", "1/2"], ["of", "1 of 2"], ["page", "Page 1 of 2"]].map(([v, lbl]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`s-cont-style-${site.id}-${v}`}
                  onClick={() => setContStyle(v)}
                  className={`text-[11px] font-mono px-3 py-1 rounded border ${
                    contStyle === v
                      ? "bg-eucalyptus-900 text-white border-eucalyptus-900"
                      : "border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-charcoal-soft mt-2">
            Height 0 = auto (continuous rolls). Landscape swaps dimensions at print time.
          </div>

          {/* Printer calibration — fine X/Y nudge (mm) via the TSPL
              REFERENCE command. Fixes media that sits a few mm off the
              print head (content landing left / high on the roll). */}
          <div className="mt-4 pt-3 border-t border-[color:var(--border)]" data-testid={`s-calib-${site.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <Crosshair className="w-4 h-4 text-ochre-600" />
              <span className="label-eyebrow">Print calibration (mm)</span>
            </div>
            <p className="text-[11px] text-charcoal-soft mb-2">
              Nudge the whole label if it prints off-centre. + X moves content right, + Y moves it down.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-mono text-charcoal-soft">Horizontal (X)</span>
                  <span className="text-[11px] font-mono text-eucalyptus-900">{offsetX >= 0 ? "+" : ""}{offsetX}mm</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    data-testid={`s-calib-x-minus-${site.id}`}
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => saveCalibration(Number(offsetX) - 0.5, offsetY)}
                    className="h-9 w-9 p-0 shrink-0"
                    title="Move content 0.5mm left"
                  >−</Button>
                  <Input
                    data-testid={`s-calib-x-${site.id}`}
                    type="number" inputMode="decimal" min="-20" max="20" step="0.5"
                    value={offsetX}
                    onChange={(e) => setOffsetX(e.target.value)}
                    onBlur={() => saveCalibration(offsetX, offsetY)}
                    className="h-9 font-mono no-spin text-center text-base"
                    aria-label="Horizontal offset (mm)"
                  />
                  <Button
                    data-testid={`s-calib-x-plus-${site.id}`}
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => saveCalibration(Number(offsetX) + 0.5, offsetY)}
                    className="h-9 w-9 p-0 shrink-0"
                    title="Move content 0.5mm right"
                  >+</Button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-mono text-charcoal-soft">Vertical (Y)</span>
                  <span className="text-[11px] font-mono text-eucalyptus-900">{offsetY >= 0 ? "+" : ""}{offsetY}mm</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    data-testid={`s-calib-y-minus-${site.id}`}
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => saveCalibration(offsetX, Number(offsetY) - 0.5)}
                    className="h-9 w-9 p-0 shrink-0"
                    title="Move content 0.5mm up"
                  >−</Button>
                  <Input
                    data-testid={`s-calib-y-${site.id}`}
                    type="number" inputMode="decimal" min="-20" max="20" step="0.5"
                    value={offsetY}
                    onChange={(e) => setOffsetY(e.target.value)}
                    onBlur={() => saveCalibration(offsetX, offsetY)}
                    className="h-9 font-mono no-spin text-center text-base"
                    aria-label="Vertical offset (mm)"
                  />
                  <Button
                    data-testid={`s-calib-y-plus-${site.id}`}
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => saveCalibration(offsetX, Number(offsetY) + 0.5)}
                    className="h-9 w-9 p-0 shrink-0"
                    title="Move content 0.5mm down"
                  >+</Button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <Button
                data-testid={`s-calib-reset-${site.id}`}
                size="sm" variant="ghost" disabled={busy}
                onClick={() => saveCalibration(0, 0)}
                className="h-8 text-xs text-charcoal-soft"
              >Reset to 0</Button>
              <span className="text-[11px] text-charcoal-soft self-center">
                Then hit <b>Test on Tecor</b> below to check alignment.
              </span>
            </div>
          </div>

          <div className="flex gap-2 mt-2">
            <Button
              data-testid={`s-label-save-${site.id}`}
              size="sm"
              disabled={busy}
              onClick={saveLabelSize}
              className="flex-1 h-9 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1"
            >
              <Save className="w-4 h-4" /> Save
            </Button>
            <Button
              data-testid={`s-label-tecor-${site.id}`}
              size="sm"
              onClick={testPrintHub}
              className="flex-1 h-9 bg-ochre-600 hover:bg-ochre-400 text-white gap-1"
              title="Send the 60×50mm calibration label to the Print Hub"
            >
              <Usb className="w-4 h-4" /> Batch print test
            </Button>
          </div>
          <p className="text-[11px] text-charcoal-soft mt-2">
            Standard sizes: <b>80×0</b> docket rolls (default), <b>60×40</b> freezer
            labels, <b>40×30</b> shelf tags, <b>102×152</b> shipping. Applied
            server-side when sending to the thermal printer.
          </p>
        </div>

        {/* Public Trust page opt-in — turns this site's live compliance
            into a shareable URL the restaurant can put on their menu /
            Google profile. Default OFF, per-site. */}
        <div className="mt-4 pt-3 border-t border-[color:var(--border)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Share2 className="w-4 h-4 text-eucalyptus-700" />
                <span className="label-eyebrow">Public trust page</span>
              </div>
              <p className="text-xs text-charcoal-soft mt-1 max-w-xs">
                Lets customers see live cold-chain compliance for this site. Off until you're ready to share.
              </p>
            </div>
            <Switch
              data-testid={`s-trust-toggle-${site.id}`}
              checked={trustOn}
              disabled={busy}
              onCheckedChange={toggleTrust}
            />
          </div>
          {trustOn && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`s-trust-url-${site.id}`}>
              <code className="font-mono text-[11px] flex-1 min-w-0 truncate bg-bone-alt px-2 py-1.5 rounded border border-[color:var(--border)]">
                {trustUrl}
              </code>
              <Button
                data-testid={`s-trust-copy-${site.id}`}
                size="sm"
                variant="outline"
                onClick={copyTrustUrl}
                className="h-8 gap-1 border-eucalyptus-700 text-eucalyptus-900"
              >
                <Copy className="w-3 h-3" /> Copy
              </Button>
              <Button
                data-testid={`s-trust-view-${site.id}`}
                size="sm"
                variant="outline"
                onClick={() => window.open(trustUrl, "_blank", "noopener")}
                className="h-8 gap-1 border-eucalyptus-700 text-eucalyptus-900"
              >
                <Eye className="w-3 h-3" /> View
              </Button>
            </div>
          )}
        </div>
      </div>
      <Button data-testid={`s-del-${site.id}`} variant="ghost" size="icon" onClick={() => onDelete(site.id)}
        className="text-ochre-600"><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
}

function UsersPanel() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "staff", site_ids: [] });
  const load = async () => {
    const [u, s] = await Promise.all([api.get("/users"), api.get("/sites")]);
    setItems(u.data);
    setSites(s.data);
  };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", f);
      toast.success("User added");
      setF({ name: "", email: "", password: "", role: "staff", site_ids: [] });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    await api.delete(`/users/${id}`); toast.success("Deleted"); load();
  };
  const updateSites = async (uid, site_ids) => {
    try {
      await api.patch(`/users/${uid}`, { site_ids });
      toast.success("Site access updated");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const toggleNewSite = (sid) => {
    setF((x) => ({
      ...x,
      site_ids: x.site_ids.includes(sid)
        ? x.site_ids.filter((i) => i !== sid)
        : [...x.site_ids, sid],
    }));
  };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="surface p-6 grid md:grid-cols-5 gap-3">
        <div className="md:col-span-5 flex items-center gap-2">
          <Users className="w-5 h-5 text-eucalyptus-700" />
          <h3 className="font-display font-bold">Add user</h3>
        </div>
        <Input data-testid="u-name" placeholder="Name" value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12" required />
        <Input data-testid="u-email" type="email" placeholder="Email" value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12" required />
        <Input data-testid="u-password" type="password" placeholder="Password" value={f.password}
          onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12" required />
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
          <SelectTrigger data-testid="u-role" className="h-12"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin (Food Safety Supervisor)</SelectItem>
            <SelectItem value="staff">Staff (Kitchen)</SelectItem>
            <SelectItem value="auditor">Auditor (Read-only)</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" data-testid="u-submit"
          className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1">
          <Plus className="w-4 h-4" /> Add
        </Button>
        <div className="md:col-span-5 surface-alt p-3">
          <div className="label-eyebrow mb-2">Site access (leave empty for admin to access all sites)</div>
          <div className="flex flex-wrap gap-2">
            {sites.map((s) => (
              <label key={s.id} className={`flex items-center gap-2 text-xs border px-3 py-1.5 rounded-md cursor-pointer ${f.site_ids.includes(s.id) ? "bg-eucalyptus-900 text-white border-eucalyptus-900" : "bg-white border-[color:var(--border)]"}`}>
                <input type="checkbox" className="sr-only" checked={f.site_ids.includes(s.id)} onChange={() => toggleNewSite(s.id)} />
                {s.name}
              </label>
            ))}
          </div>
        </div>
      </form>

      <div className="surface overflow-hidden">
        <div className="grid grid-cols-12 bg-bone-alt px-6 py-3 text-[11px] uppercase font-bold tracking-widest text-eucalyptus-700">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-4">Site access</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {items.map((u) => (
          <UserRow key={u.id} u={u} sites={sites} onDelete={del} onUpdateSites={updateSites} />
        ))}
      </div>
    </div>
  );
}

function UserRow({ u, sites, onDelete, onUpdateSites }) {
  const [selected, setSelected] = useState(u.site_ids || []);
  const [dirty, setDirty] = useState(false);
  const toggle = (sid) => {
    setDirty(true);
    setSelected((s) => s.includes(sid) ? s.filter((x) => x !== sid) : [...s, sid]);
  };
  return (
    <div className="grid grid-cols-12 px-6 py-3 items-center border-t border-[color:var(--border)]">
      <div className="col-span-3 font-medium">{u.name}</div>
      <div className="col-span-3 text-sm">{u.email}</div>
      <div className="col-span-1 capitalize text-sm">{u.role}</div>
      <div className="col-span-4 flex flex-wrap gap-1.5 items-center">
        {sites.map((s) => (
          <button
            key={s.id}
            data-testid={`u-site-${u.id}-${s.id}`}
            type="button"
            onClick={() => toggle(s.id)}
            className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${selected.includes(s.id) ? "bg-eucalyptus-900 text-white" : "bg-bone-alt text-charcoal-soft"}`}
          >
            {s.name.split(" ")[0]}
          </button>
        ))}
        {dirty && (
          <Button
            data-testid={`u-sites-save-${u.id}`}
            size="sm"
            onClick={() => { onUpdateSites(u.id, selected); setDirty(false); }}
            className="h-7 text-xs bg-ochre-600 hover:bg-ochre-400 text-white"
          >
            Save
          </Button>
        )}
      </div>
      <div className="col-span-1 text-right">
        <Button data-testid={`u-del-${u.id}`} variant="ghost" size="icon" onClick={() => onDelete(u.id)}
          className="text-ochre-600"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function SuppliersPanel({ siteId }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", abn: "", contact: "", phone: "", products: "", approved: true });
  const load = useCallback(async () => {
    const { data } = await api.get("/suppliers", withSite(siteId));
    setItems(data);
  }, [siteId]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/suppliers", f, withSite(siteId));
      toast.success("Supplier added");
      setF({ name: "", abn: "", contact: "", phone: "", products: "", approved: true });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/suppliers/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="surface p-6 grid md:grid-cols-4 gap-3">
        <div className="md:col-span-4 flex items-center gap-2">
          <Truck className="w-5 h-5 text-eucalyptus-700" />
          <h3 className="font-display font-bold">Add supplier</h3>
        </div>
        <Input data-testid="sup-name" placeholder="Name" value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12" required />
        <Input data-testid="sup-abn" placeholder="ABN" value={f.abn}
          onChange={(e) => setF({ ...f, abn: e.target.value })} className="h-12" />
        <Input data-testid="sup-contact" placeholder="Contact" value={f.contact}
          onChange={(e) => setF({ ...f, contact: e.target.value })} className="h-12" />
        <Input data-testid="sup-phone" placeholder="Phone" value={f.phone}
          onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-12" />
        <Input data-testid="sup-products" placeholder="Products supplied" value={f.products}
          onChange={(e) => setF({ ...f, products: e.target.value })} className="h-12 md:col-span-3" />
        <Button type="submit" data-testid="sup-submit"
          className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </form>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((s) => (
          <div key={s.id} className="surface p-4 flex items-start justify-between">
            <div>
              <div className="font-display font-bold">{s.name}</div>
              <div className="text-xs text-charcoal-soft">{s.abn}</div>
              <div className="text-xs mt-1">{s.products}</div>
            </div>
            <Button data-testid={`sup-del-${s.id}`} variant="ghost" size="icon" onClick={() => del(s.id)}
              className="text-ochre-600"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EquipmentPanel({ siteId }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", type: "fridge", location: "", target_min_c: 0, target_max_c: 5, barcode_id: "" });
  const [editing, setEditing] = useState({});
  const [eqEdit, setEqEdit] = useState({});   // full-record drafts keyed by equipment id
  const [siteName, setSiteName] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get("/equipment", withSite(siteId));
    setItems(data);
    // Fetch site name so printed stickers carry the same header the
    // thermal path uses — done once, non-fatal if it fails.
    try {
      const s = await api.get(`/sites/${siteId}`);
      if (s?.data?.name) setSiteName(s.data.name);
    } catch { /* keep empty */ }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/equipment", {
        ...f,
        target_min_c: parseFloat(f.target_min_c),
        target_max_c: parseFloat(f.target_max_c),
      }, withSite(siteId));
      toast.success("Equipment added");
      setF({ name: "", type: "fridge", location: "", target_min_c: 0, target_max_c: 5, barcode_id: "" });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/equipment/${id}`); toast.success("Deleted"); load(); };
  const saveBarcode = async (id) => {
    const code = (editing[id] || "").trim();
    if (!code) { toast.error("Enter a barcode ID first"); return; }
    try {
      await api.patch(`/equipment/${id}`, { barcode_id: code });
      toast.success("Barcode ID updated");
      setEditing((s) => { const n = { ...s }; delete n[id]; return n; });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const eqPayload = (eq) => ({
    label_type: "equipment",
    equipment_name: eq.name,
    equipment_barcode: eq.barcode_id || eq.id,
    equipment_type: eq.type,
    equipment_location: eq.location,
    equipment_target_min: eq.target_min_c,
    equipment_target_max: eq.target_max_c,
    // QR deep-link → opens the temperature form pre-filled for this
    // equipment when scanned with a phone camera. Short `/e/<id>` path
    // keeps the printed QR small + scannable.
    qr_url: `${window.location.origin}/e/${eq.id}`,
    copies: 1,
  });
  const printBarcodeTecor = async (id) => {
    const eq = items.find((x) => x.id === id);
    if (!eq) return;
    try {
      await enqueuePrint(siteId, {
        label_type: "equipment", product_name: eq.name, copies: 1, payload: eqPayload(eq),
      });
      toast.success(`${eq.name} sent to Print Hub`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message || "Couldn't queue");
    }
  };
  const printAllBarcodesTecor = async () => {
    if (!items.length) return;
    try {
      for (const eq of items) {
        // eslint-disable-next-line no-await-in-loop
        await enqueuePrint(siteId, {
          label_type: "equipment", product_name: eq.name, copies: 1, payload: eqPayload(eq),
        });
      }
      toast.success(`${items.length} equipment stickers sent to Print Hub`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message || "Couldn't queue");
    }
  };

  // Full-equipment edit: name / type / location / target range. Barcode is
  // still edited via its own dedicated block so the visual affordance
  // ("this is a scan-target") stays distinct.
  const startEdit = (eq) => {
    setEqEdit((s) => ({
      ...s,
      [eq.id]: {
        name: eq.name || "",
        type: eq.type || "fridge",
        location: eq.location || "",
        target_min_c: eq.target_min_c ?? 0,
        target_max_c: eq.target_max_c ?? 5,
      },
    }));
  };
  const cancelEdit = (id) => {
    setEqEdit((s) => { const n = { ...s }; delete n[id]; return n; });
  };
  const saveEdit = async (id) => {
    const draft = eqEdit[id];
    if (!draft) return;
    if (!draft.name?.trim()) { toast.error("Name is required"); return; }
    const min = parseFloat(draft.target_min_c);
    const max = parseFloat(draft.target_max_c);
    if (Number.isNaN(min) || Number.isNaN(max)) { toast.error("Target range must be numbers"); return; }
    if (min > max) { toast.error("Min °C must be ≤ Max °C"); return; }
    try {
      await api.patch(`/equipment/${id}`, {
        name: draft.name.trim(),
        type: draft.type,
        location: draft.location.trim(),
        target_min_c: min,
        target_max_c: max,
      });
      toast.success("Equipment updated");
      cancelEdit(id);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="surface p-6 grid md:grid-cols-5 gap-3">
        <div className="md:col-span-5 flex items-center gap-2">
          <Refrigerator className="w-5 h-5 text-eucalyptus-700" />
          <h3 className="font-display font-bold">Add equipment</h3>
        </div>
        <Input data-testid="eq-name" placeholder="Name" value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12" required />
        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
          <SelectTrigger data-testid="eq-type" className="h-12"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fridge">Fridge</SelectItem>
            <SelectItem value="freezer">Freezer</SelectItem>
            <SelectItem value="cool_room">Cool room</SelectItem>
            <SelectItem value="hot_holding">Hot holding</SelectItem>
            <SelectItem value="oven">Oven</SelectItem>
            <SelectItem value="bain_marie">Bain marie</SelectItem>
            <SelectItem value="blast_chiller">Blast chiller</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="eq-loc" placeholder="Location" value={f.location}
          onChange={(e) => setF({ ...f, location: e.target.value })} className="h-12" />
        <Input data-testid="eq-min" type="number" step="0.1" placeholder="Min °C" value={f.target_min_c}
          onChange={(e) => setF({ ...f, target_min_c: e.target.value })} className="h-12 font-mono" />
        <Input data-testid="eq-max" type="number" step="0.1" placeholder="Max °C" value={f.target_max_c}
          onChange={(e) => setF({ ...f, target_max_c: e.target.value })} className="h-12 font-mono" />
        <Input data-testid="eq-barcode" placeholder="Barcode ID (optional)" value={f.barcode_id}
          onChange={(e) => setF({ ...f, barcode_id: e.target.value })} className="h-12 font-mono md:col-span-4" />
        <div className="md:col-span-5 flex justify-end">
          <Button type="submit" data-testid="eq-submit"
            className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1">
            <Plus className="w-4 h-4" /> Add equipment
          </Button>
        </div>
      </form>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-charcoal-soft">
          <span className="font-mono">{items.length}</span> pieces of equipment on this site.
          Each one auto-mints a barcode ID so you can print stickers and scan from your phone.
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="eq-tecor-all"
            onClick={printAllBarcodesTecor}
            disabled={!items.length}
            className="h-10 gap-2 bg-ochre-600 hover:bg-ochre-400 text-white"
          >
            <Usb className="w-4 h-4" /> Batch print all
          </Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((e) => {
          const draft = editing[e.id];
          const isEditing = draft !== undefined;
          const rowDraft = eqEdit[e.id];
          const isRowEditing = rowDraft !== undefined;
          return (
            <div key={e.id} className="surface p-4" data-testid={`eq-card-${e.id}`}>
              {isRowEditing ? (
                <div className="space-y-2" data-testid={`eq-edit-form-${e.id}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      data-testid={`eq-edit-name-${e.id}`}
                      value={rowDraft.name}
                      onChange={(ev) => setEqEdit((s) => ({ ...s, [e.id]: { ...s[e.id], name: ev.target.value } }))}
                      placeholder="Name"
                      className="h-10 col-span-2"
                    />
                    <Select
                      value={rowDraft.type}
                      onValueChange={(v) => setEqEdit((s) => ({ ...s, [e.id]: { ...s[e.id], type: v } }))}
                    >
                      <SelectTrigger data-testid={`eq-edit-type-${e.id}`} className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fridge">Fridge</SelectItem>
                        <SelectItem value="freezer">Freezer</SelectItem>
                        <SelectItem value="cool_room">Cool room</SelectItem>
                        <SelectItem value="hot_holding">Hot holding</SelectItem>
                        <SelectItem value="oven">Oven</SelectItem>
                        <SelectItem value="bain_marie">Bain marie</SelectItem>
                        <SelectItem value="blast_chiller">Blast chiller</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      data-testid={`eq-edit-loc-${e.id}`}
                      value={rowDraft.location}
                      onChange={(ev) => setEqEdit((s) => ({ ...s, [e.id]: { ...s[e.id], location: ev.target.value } }))}
                      placeholder="Location"
                      className="h-10"
                    />
                    <Input
                      data-testid={`eq-edit-min-${e.id}`}
                      type="number"
                      step="0.1"
                      value={rowDraft.target_min_c}
                      onChange={(ev) => setEqEdit((s) => ({ ...s, [e.id]: { ...s[e.id], target_min_c: ev.target.value } }))}
                      placeholder="Min °C"
                      className="h-10 font-mono"
                    />
                    <Input
                      data-testid={`eq-edit-max-${e.id}`}
                      type="number"
                      step="0.1"
                      value={rowDraft.target_max_c}
                      onChange={(ev) => setEqEdit((s) => ({ ...s, [e.id]: { ...s[e.id], target_max_c: ev.target.value } }))}
                      placeholder="Max °C"
                      className="h-10 font-mono"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      data-testid={`eq-edit-cancel-${e.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelEdit(e.id)}
                      className="h-9 gap-1 text-charcoal-soft"
                    >
                      <X className="w-4 h-4" /> Cancel
                    </Button>
                    <Button
                      data-testid={`eq-edit-save-${e.id}`}
                      size="sm"
                      onClick={() => saveEdit(e.id)}
                      className="h-9 gap-1 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
                    >
                      <Save className="w-4 h-4" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="label-eyebrow">{e.type.replace("_", " ")}</div>
                    <div className="font-display font-bold mt-1 truncate">{e.name}</div>
                    <div className="text-xs text-charcoal-soft">{e.location}</div>
                    <div className="text-xs font-mono mt-1">{e.target_min_c}°C – {e.target_max_c}°C</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      data-testid={`eq-edit-${e.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(e)}
                      className="text-eucalyptus-900"
                      title="Edit equipment"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`eq-del-${e.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => del(e.id)}
                      className="text-ochre-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-[color:var(--border)]">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-eucalyptus-700" />
                  <span className="label-eyebrow">Barcode ID</span>
                </div>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Input
                      data-testid={`eq-barcode-input-${e.id}`}
                      value={draft}
                      onChange={(ev) => setEditing((s) => ({ ...s, [e.id]: ev.target.value }))}
                      className="h-10 font-mono"
                      placeholder="EQ-FRIDGE-01"
                    />
                    <Button
                      data-testid={`eq-barcode-save-${e.id}`}
                      onClick={() => saveBarcode(e.id)}
                      className="h-10 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-1"
                    >
                      <Save className="w-4 h-4" /> Save
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <code className="font-mono text-sm" data-testid={`eq-barcode-${e.id}`}>
                      {e.barcode_id || "—"}
                    </code>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`eq-barcode-edit-${e.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing((s) => ({ ...s, [e.id]: e.barcode_id || "" }))}
                        className="h-9 border-eucalyptus-700 text-eucalyptus-900"
                      >
                        Edit code
                      </Button>
                      <Button
                        data-testid={`eq-barcode-tecor-${e.id}`}
                        size="sm"
                        onClick={() => printBarcodeTecor(e.id)}
                        className="h-9 bg-ochre-600 hover:bg-ochre-400 text-white gap-1"
                        title="Send this sticker to the Print Hub"
                      >
                        <Usb className="w-4 h-4" /> Batch print
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "linux";
  const ua = navigator.userAgent || "";
  if (/Win/i.test(ua)) return "windows";
  if (/Mac/i.test(ua)) return "macos";
  return "linux";
}

function BridgePanel() {
  const [url, setUrl] = useState(getBridgeUrl());
  const [status, setStatus] = useState(null);
  const [installPlatform, setInstallPlatform] = useState(detectPlatform());

  const save = (nextUrl) => {
    const v = (nextUrl ?? url).trim();
    setBridgeUrl(v);
    setUrl(v);
    toast.success(v ? "Bridge URL saved" : "Bridge URL cleared");
  };

  const test = async (target) => {
    const u = (target ?? url).trim();
    if (!u) {
      setStatus("no-url");
      return;
    }
    setStatus("checking");
    try {
      const r = await fetch(`${u.replace(/\/$/, "")}/healthz`, { method: "GET" });
      setStatus(r.ok ? "ok" : `error-${r.status}`);
    } catch {
      setStatus("unreachable");
    }
  };

  const downloadInstaller = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/kitchen-bridge/installer/${installPlatform}`, "_blank");
    toast.success("Installer downloaded — run it on this device, then click Connect.");
  };

  const connect = async () => {
    const target = "http://localhost:9101";
    save(target);
    await test(target);
  };

  const platformLabel = {
    windows: "Windows (PowerShell)",
    macos: "macOS (Terminal)",
    linux: "Linux (Terminal)",
  }[installPlatform];

  const runHint = {
    windows: "Double-click haccp-bridge-install.bat. Approve the Windows prompt.",
    macos: "In Finder, right-click haccp-bridge-install.command → Open → Open (once).",
    linux: "bash haccp-bridge-install.sh   (or chmod +x then double-click)",
  }[installPlatform];

  return (
    <div className="space-y-6">
      <div className="surface p-6" data-testid="kb-install-card">
        <div className="flex items-center gap-2 mb-2">
          <Download className="w-5 h-5 text-eucalyptus-700" />
          <h3 className="font-display font-bold">Install Kitchen Bridge on this device</h3>
        </div>
        <p className="text-sm text-charcoal-soft mb-4 max-w-2xl">
          The bridge lets labels print over the LAN even when the cloud is unreachable.
          One-click installer drops a tiny Node service into your user folder, registers
          autostart and brings it up on{" "}
          <code className="font-mono">http://localhost:9101</code>. Requires Node.js 18+ on the device.
        </p>

        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-widest font-bold text-eucalyptus-700">
              Detected OS
            </label>
            <Select value={installPlatform} onValueChange={setInstallPlatform}>
              <SelectTrigger data-testid="kb-platform" className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="windows">Windows</SelectItem>
                <SelectItem value="macos">macOS</SelectItem>
                <SelectItem value="linux">Linux</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="kb-download"
            onClick={downloadInstaller}
            className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
          >
            <Download className="w-4 h-4" /> Download installer
          </Button>
          <Button
            data-testid="kb-connect"
            onClick={connect}
            className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white gap-2"
          >
            <Cable className="w-4 h-4" /> Connect (after install)
          </Button>
        </div>

        <ol className="mt-5 grid md:grid-cols-3 gap-3 text-xs">
          <li className="surface-alt p-4">
            <div className="label-eyebrow">Step 1</div>
            <div className="mt-2">Click <b>Download installer</b> for {platformLabel}.</div>
          </li>
          <li className="surface-alt p-4">
            <div className="label-eyebrow">Step 2</div>
            <div className="mt-2">
              {installPlatform === "linux" ? "Run on the kitchen device:" : "On the kitchen device:"}
            </div>
            <code className="block mt-1 font-mono text-[11px] bg-white p-2 rounded border border-[color:var(--border)] break-all">
              {runHint}
            </code>
          </li>
          <li className="surface-alt p-4">
            <div className="label-eyebrow">Step 3</div>
            <div className="mt-2">
              Click <b>Connect</b>. We&apos;ll set the URL to{" "}
              <code className="font-mono">http://localhost:9101</code> and verify the bridge is healthy.
            </div>
          </li>
        </ol>
      </div>

      <div className="surface p-6">
        <div className="flex items-center gap-2 mb-3">
          <Cable className="w-5 h-5 text-eucalyptus-700" />
          <h3 className="font-display font-bold">Bridge URL</h3>
        </div>
        <p className="text-sm text-charcoal-soft mb-5 max-w-2xl">
          Override the URL if the bridge runs on a different host or port.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <Input
            data-testid="kb-url"
            placeholder="http://localhost:9101"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="h-12 font-mono md:col-span-2"
          />
          <div className="flex gap-2">
            <Button
              data-testid="kb-save"
              onClick={() => save()}
              className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white flex-1"
            >
              Save
            </Button>
            <Button
              data-testid="kb-test"
              onClick={() => test()}
              variant="outline"
              className="h-12 border-eucalyptus-700 text-eucalyptus-900"
            >
              Test
            </Button>
          </div>
        </div>
        {status && (
          <div className="mt-3 text-xs font-mono" data-testid="kb-status">
            {status === "checking" && "Checking..."}
            {status === "ok" && (
              <span className="text-eucalyptus-700">Bridge reachable and healthy.</span>
            )}
            {status === "no-url" && (
              <span className="text-ochre-600">Set a URL first.</span>
            )}
            {status === "unreachable" && (
              <span className="text-ochre-600">
                Bridge not reachable. Is the installer running on this device?
              </span>
            )}
            {status?.startsWith?.("error-") && (
              <span className="text-ochre-600">
                Bridge responded with HTTP {status.replace("error-", "")}.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

